import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { PeopleProfileService } from "../../services/people-profile/people-profile.service";
import {AlertBannerService, AlertType } from "../../lib/alert-banner";
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { PermissionsService } from "../../services/permissions/permissions.service";
import * as _ from 'lodash';
import moment from 'moment-timezone';
import { ConstantsService } from 'src/app/services/constants/constants.service';
import { OptInProfileDetailModalComponent } from "../opt-in-profile-detail-modal/opt-in-profile-detail-modal.component";
import {
  PeopleProfileEditDetailsModalComponent
} from "../people-profile-edit-details-modal/people-profile-edit-details-modal/people-profile-edit-details-modal.component";
import { WarningModalContentComponent } from "../../lib/warning-modal/warning-modal.component";
import { forkJoin } from "rxjs";
import { OrganisationService } from "../../services/organisation/organisation.service";

@Component({
  selector: 'app-enhanced-people-profile-snapshot',
  templateUrl: './enhanced-people-profile-snapshot.component.pug',
  styleUrls: ['./enhanced-people-profile-snapshot.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class EnhancedPeopleProfileSnapshotComponent implements OnInit {
  @Input() profile: any;
  @Input() profileId: string | null = '';
  @Input() doNotContact!: boolean;

  statuses: any = [];
  selectedStatus = {label: 'Unknown', colour: 'black'};
  maxCompetencies = 7;
  optInProfileDetailModal: BsModalRef = new BsModalRef();
  selectedTab: any = 'all';
  snapFields: any = [];
  snapData: any = {};
  domains: any;
  departments: any;
  enableEdit = false;
  editWarningModal: BsModalRef = new BsModalRef();
  peopleProfileEditModal: BsModalRef = new BsModalRef();
  LESS_THAN_1_MONTH = $localize`Less than a month`;
  canViewReferenceCompetencyScore = false;
  canViewExitCompetencyScore = false;
  canEditPeopleProfile = false;
  disableIfOnlyAnalystOrNoAccess = false;
  OptInTime = '';
  profileStatusMap = {
    'Candidate': 3,
    'Employee': 1,
    'Ex-employee': 0,
    'Unspecified': 5,
    'Potential Candidate': 4,
  };
  allowRehireEdit = false;
  roleLabel = $localize`Role`;
  peopleProfileShowModal: BsModalRef = new BsModalRef();
  editSuccessModal: BsModalRef = new BsModalRef();

  constructor(
    private peopleProfile: PeopleProfileService,
    private alertList: AlertBannerService,
    public permissions: PermissionsService,
    private modalService: BsModalService,
    private organisationService: OrganisationService,
  ) {
  }

  ngOnInit(): void {
    this.disableIfOnlyAnalystOrNoAccess = this.checkIfAllRolesAreMemberAnalystOrNone(this.permissions.role);
    this.profile['person_details']['created_formatted'] = moment(this.profile['person_details']['created']).format('ll');
    this.maxCompetencies = this.profile['profile']['status'] === 'Ex-employee' ? 5 : 7;
    this.snapFields = ConstantsService.NEW_PROFILE_FIELDS[this.profile.profile.status.toLowerCase()] || [];
    this.snapFields.push({
        label: $localize`Organisation`,
        key: 'organisation_name'
      });
    this.snapData = this.extractDataFromProfile(this.profile);
    this.OptInTime = this.formatTimestamp(this.profile.profile.opt_in_time) ?? '';
    this.canViewReferenceCompetencyScore = this.permissions.hasPermission('can_view_reference_competencies_score')
    this.canViewExitCompetencyScore = this.permissions.hasPermission('can_view_exit_competencies_score')
    this.canEditPeopleProfile = this.permissions.hasPermission('can_edit_people_profile')
    if (this.snapData.status === 'Unspecified') {
      this.snapData.status = 'Check recipient';
    }
    forkJoin({
      orgDetails: this.organisationService.getOrganisation(),
      orgDepartments: this.organisationService.getAllDepartments()
    })
    .subscribe(({ orgDetails, orgDepartments }) => {
      this.domains = orgDetails['domains'].map((item) => {
        return { label: item, value: `@${item}` }
      });
      this.departments = orgDepartments['departments'].map((department) => {
        return { key: department.id, value: department.department };
      });
      this.enableEdit = true;
    });
  }

  formatTimestamp(timestamp, format = 'DD MMM YYYY') {
  if (!timestamp) return null;

  const date = new Date(timestamp);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}


  showDetails() {
    const potentialCandidateData = {
      'potential_candidate_role': this.profile.profile.job_title,
      'potential_candidate_opt_in_time': this.OptInTime,
      'potential_candidate_company_name': this.profile.profile.organisation_name,
      'potential_candidate_preferred_role': this.profile.profile.preferred_role,
      'potential_candidate_additional_info': this.profile.profile.additional_info,
      'potential_candidate_location': this.snapData?.location,
    }
    const exEmployeeData = {
      'ex_employee_opt_in_time': this.OptInTime,
      'ex_employee_additional_info': this.profile.profile.additional_info,
      'ex_employee_company_name': this.profile.profile.organisation_name,
      'ex_employee_preferred_role': this.profile.profile.preferred_role,
      'ex_employee_preferred_industry': this.profile.profile.preferred_industry,
      'ex_employee_location': this.snapData?.location,

    }
    const initialState = {
      'potentialCandidateData': potentialCandidateData,
      'exEmployeeData': exEmployeeData,
      'details': this.profile.person_details,
      'status': this.profile.profile.status,
    }
    this.optInProfileDetailModal = this.modalService.show(
      OptInProfileDetailModalComponent, {
        class: "modal-dialog-centered default-modal new-switch-org-modal",
        initialState
      }
    )
  }

  openEmail(email: string): void {
    if (!email) {
      console.error('No email address provided');
      return;
    }

    const mailtoLink = `mailto:${email}`;

    window.open(mailtoLink, '_blank');

  }

  openLinkedin(linkedin: string): void {
    if (this.disableIfOnlyAnalystOrNoAccess) {
      return;
    }
    if (!linkedin) {
      console.error('No LinkedIn URL provided');
      return;
    }

    window.open(linkedin, '_blank');
  }

  checkOverflow(element: any): boolean {
    return element.offsetWidth < element.scrollWidth;
  }

  openGoogle(firstName: string, lastName: string, role: string): void {
    // Early return if access is disabled
    if (this.disableIfOnlyAnalystOrNoAccess) {
      return;
    }
    // Validate required parameters
    if (!firstName || !lastName) {
      console.error('No name provided');
      return;
    }

    // Build search query
    const fullName = `${firstName} ${lastName}`;
    const searchQuery = role
      ? `"${fullName}" "${role}"`
      : `"${fullName}"`;

    const googleSearch = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;

    // Open in new tab
    window.open(googleSearch, '_blank');
  }

  checkIfAllRolesAreMemberAnalystOrNone(role) {
    // If there's no role or no products, return false
    if (!role || !role.products) {
      return false;
    }

    // Check if role is "member"
    if (role.role !== "member") {
      return false;
    }

    // Check all products to ensure they're either 5 (Member-Analyst) or 0 (Member-None)
    const products = role.products;

    // Flag for tracking if any product has a role other than 5 or 0
    let allRolesValid = true;

    // Check each product
    for (const productName in products) {
      const productRole = products[productName];

      // If the role is not 5 (Member-Analyst) or 0 (Member-None), set flag to false
      if (productRole !== 5 && productRole !== 0) {
        allRolesValid = false;
        break;
      }
    }

    return allRolesValid;
  }

  extractDataFromProfile(profile: any): any {
    let _result = Object.create(null);
    _.forEach(this.getKeysFromField(this.snapFields), function (_key) {
      _.set(_result, _key, _.get({
        ...profile.person_details,
        ...profile.profile,
      }, _key, ''));
    });
    _result.created = moment(_result.created).format('ll');
    _result.work_email = profile.person_details.emails_with_types?.work;
    _result.personal_email = profile.person_details.emails_with_types?.personal;
    _result.tenure = this.getDateDifference(profile.person_details.employment_end_date, profile.person_details.employment_start_date);
    _result.max_competency = this.getMaxCompetency(profile.profile);
    return _result;
  }

  getMaxCompetency(profile) {
    if (profile.status === 'Candidate') {
      return 7;
    } else if (profile.status === 'Ex-employee') {
      return 5;
    }
    return null;
  }

  getKeysFromField(fields: any): Array<string> {
    let keys = Array<string>();
    fields.map((_) => {
      keys.push(_.key);
    })
    return keys;
  }

  getDateDifference(end: any, start: any) {
    if (start !== null) {
      if (end !== null && start !== null) {
        return this.calculate_date_difference(start, end);
      } else {
        end = moment().format('YYYY-MM-DD');
        return this.calculate_date_difference(start, end);
      }
    }
    return '';
  }

  editDetails() {
    let details = this.profile.person_details;
    const initialState = {
      'details': details,
      'profile': this.profile.profile,
      'snapstate': this.profile,
      'domains': this.domains,
      'departments': this.departments,
      'doNotContact': this.snapData?.do_not_contact,
    }
    this.peopleProfileEditModal = this.modalService.show(
      PeopleProfileEditDetailsModalComponent, {class: "modal-dialog-centered min-width-554", initialState}
    );

    this.peopleProfileEditModal.content.saveClick.subscribe(res => {
      // Check if status change requires a warning
      if ([0, 1, 3].includes(res.status) && res.status != this.profileStatusMap[this.profile.profile.status]) {
        const formatted_employment_start_date = moment(res.employment_start_date).format('ll');
        const formatted_employment_end_date = moment(res.employment_end_date).format('ll');
        const title = $localize`Edit profile status`;
        let State = {};

        if (res.status == 3) {
          State = {
            'title': title,
            'message': $localize`You are changing the profile status to <strong>candidate</strong>. This action will clear any previously entered data for employment start and end date.\nAre you sure you want to continue?`
          };
        } else if (res.status == 1) {
          res.employment_start_date = moment(formatted_employment_start_date, 'll').format('YYYY-MM-DD');
          State = {
            'title': title,
            'message': $localize`You are changing the profile status to <strong>employee</strong>. This action will replace any previously entered data for employment start date with ${formatted_employment_start_date} and clear any existing data for employment end date. \nAre you sure you want to continue?`
          };
        } else if (res.status == 0) {
          res.employment_end_date = moment(formatted_employment_end_date, 'll').format('YYYY-MM-DD');
          State = {
            'title': title,
            'message': $localize`You are changing the profile status to <strong>ex-employee</strong>. This action will replace any previously entered data for employment end date with ${formatted_employment_end_date}.\nAre you sure you want to continue?`
          };
        }

        // Hide the edit modal before showing the warning modal
        this.peopleProfileEditModal.hide();

        this.editWarningModal = this.modalService.show(WarningModalContentComponent,
          {id: 2, class: 'modal-dialog-centered show default-modal', initialState: {...State}}
        );

        this.editWarningModal.content.closeBtnName = 'Cancel';
        this.editWarningModal.content.agreeButton = 'Continue';

        this.editWarningModal.content.selection.subscribe(accepted => {
          if (accepted) this.updateProfile(res);
          // Hiding only the editWarningModal causes screen to freeze on irregular basis, so hiding modalService
          this.modalService.hide()
        });
      } else {
        // For non-warning cases, just update the profile and hide the edit modal
        this.peopleProfileEditModal.hide();
        this.updateProfile(res);
      }
    }, error => {
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);

      // Make sure to hide modal on error
      this.peopleProfileEditModal.hide();
    });
  }

  updateProfile(res) {
    if (this.profileId) {
      this.peopleProfile.putProfile(this.profileId, res).subscribe(
        response => {
          this.profile = response.data;
          let statusKey = Object.keys(this.profileStatusMap).find(key => this.profileStatusMap[key] === res.status);
          if (statusKey === 'Unspecified') {
            statusKey = 'Check recipient';
          }
          if (statusKey != null) {
            this.snapFields = ConstantsService.NEW_PROFILE_FIELDS[statusKey.toLowerCase()] || [];
          }
          this.snapData = this.extractDataFromProfile(this.profile);

          if (this.profile.profile.rehire_status) {
            this.snapData.rehire_status = this.profile.profile.rehire_status;
          }

          this.snapData.max_competency = this.getMaxCompetency((this.profile.profile));

          if (this.snapData.status === 'Unspecified') {
            this.snapData.status = 'Check recipient';
          }

          this.alertList.append([{
            type: AlertType.SUCCESS,
            message: $localize`Success! Profile has been updated.`,
            keepOnRouteChange: true
          }]);
        },
        err => {
          this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong.`,
            keepOnRouteChange: true
          }]);
        }
      );
    }
  }

  calculate_date_difference(start, end) {
    let start_date = moment(start);
    let end_date = moment(end);
    if (start_date.isAfter(end_date)) {
      return '';
    }
    let years = 0;
    let months = 0;
    years = end_date.diff(start_date, "years")
    start_date.add(years, 'years');
    months = end_date.diff(start_date, "months");
    let string_duration = '';
    if (years > 1) {
      string_duration = years + ' years ';
    } else if (years == 1) {
      string_duration = years + ' year ';
    }

    if (months > 1) {
      string_duration += months + ' months';
    } else if (months == 1) {
      string_duration += months + ' month';
    } else if (years == 0 && months == 0) {
      string_duration = this.LESS_THAN_1_MONTH;
    }
    return string_duration;
  }

  shareProfile(): void {
    const currentUrl = window.location.href;

    // Use the Clipboard API to copy the URL
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        this.alertList.append([{
          type: AlertType.SHARED,
          message: $localize`The profile link has been copied to your clipboard and is ready to share.`,
          timeout: 5000,
          keepOnRouteChange: true
        }]);
      });
  }


}
